import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RequirePermission from "../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../auth/adminPermissions";
import AdminPageHeader from "../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../components/midpoint/admin/layout/AdminStatePanel";
import { requestJson } from "../../api/apiClient";

function WeatherCommercialAdminPageContent() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await requestJson("/api/weather/commercial/admin/summary");
      setSummary(payload);
      setErrorMessage("");
    } catch (error) {
      setSummary(null);
      setErrorMessage(error.message || "Unable to load commercial weather summary.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = summary?.stats || {};

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title="Commercial weather intelligence"
        subtitle="Weather watches, in-app events, and report activity"
        actions={
          <Link className="admin-btn admin-btn--secondary" to="/admin/weather/sources">
            Weather sources
          </Link>
        }
      />

      {loading ? <AdminStatePanel variant="loading" message="Loading commercial metrics…" /> : null}
      {!loading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={load} />
      ) : null}

      {!loading && !errorMessage ? (
        <div className="admin-dashboard__cards">
          <div className="admin-dashboard__card">
            <div className="admin-dashboard__card-label">Total watches</div>
            <div className="admin-dashboard__card-value">{stats.total_watches ?? 0}</div>
          </div>
          <div className="admin-dashboard__card">
            <div className="admin-dashboard__card-label">Active watches</div>
            <div className="admin-dashboard__card-value">{stats.active_watches ?? 0}</div>
          </div>
          <div className="admin-dashboard__card">
            <div className="admin-dashboard__card-label">Watch events</div>
            <div className="admin-dashboard__card-value">{stats.total_events ?? 0}</div>
          </div>
          <div className="admin-dashboard__card">
            <div className="admin-dashboard__card-label">New events</div>
            <div className="admin-dashboard__card-value">{stats.new_events ?? 0}</div>
          </div>
          <div className="admin-dashboard__card">
            <div className="admin-dashboard__card-label">Elevated/critical</div>
            <div className="admin-dashboard__card-value">{stats.elevated_events ?? 0}</div>
          </div>
          <div className="admin-dashboard__card">
            <div className="admin-dashboard__card-label">Property watches</div>
            <div className="admin-dashboard__card-value">{stats.property_watches ?? 0}</div>
          </div>
        </div>
      ) : null}

      {summary?.config ? (
        <section className="panel-card">
          <p className="eyebrow">Feature flags</p>
          <ul className="muted">
            <li>Commercial enabled: {String(summary.config.commercialWeatherEnabled)}</li>
            <li>Watches enabled: {String(summary.config.weatherWatchesEnabled)}</li>
            <li>Reports enabled: {String(summary.config.weatherReportsEnabled)}</li>
            <li>Max free watches: {summary.config.maxFreeWatches}</li>
          </ul>
        </section>
      ) : null}
    </AdminPageLayout>
  );
}

export default function WeatherCommercialAdminPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.WEATHER_LAYERS_VIEW}>
      <WeatherCommercialAdminPageContent />
    </RequirePermission>
  );
}
